/**
 * Stage 0: Material Input
 * Interface for creators to upload existing materials before AI generation.
 *
 * Audio flow is two-phase server-side:
 * 1. Multi-file upload + concat (POST /upload-audio-files) → merged audio URL
 * 2. Multi-range trim + concat + transcribe (POST /transcribe-audio)
 *
 * Workflow Paths based on materials provided:
 * - Path A: Audio + English + Chinese → Whisper align → split → Parsing
 * - Path B: Audio + English → Whisper align → AI translate → split → Parsing
 * - Path C: Audio + Chinese → Whisper transcribe → split → Parsing
 * - Path D: Audio only → Whisper transcribe → AI translate → split → Parsing
 * - Path E: English text → AI translate → Parsing → TTS → merge → subtitles
 * - Path F: Chinese text → AI generate English → Parsing → TTS → merge → subtitles
 * - Path G: None → AI generate all → Parsing → TTS → merge → subtitles
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CourseDraft, MaterialSourceType } from '../../../types/creator-workflow';
import { creatorWorkflowService } from '../../../services/creatorWorkflowService';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import AudioRangeSelector, { type AudioRange } from '../AudioRangeSelector';
import MultiAudioFileList from '../MultiAudioFileList';
import './Stage0MaterialInput.css';

interface Stage0MaterialInputProps {
  draft: CourseDraft | null;
  onCreateDraft: (data: any) => Promise<CourseDraft | null>;
  onUpdateDraft: (data: any) => Promise<void>;
  onAdvanceStage: () => Promise<void>;
  loading: boolean;
  onRefreshDraft?: () => Promise<void>;
  onSetGlobalProcessing?: (isProcessing: boolean, message?: string) => void;
}

const Stage0MaterialInput: React.FC<Stage0MaterialInputProps> = ({
  draft,
  onCreateDraft,
  onUpdateDraft,
  onAdvanceStage,
  loading,
  onRefreshDraft,
  onSetGlobalProcessing,
}) => {
  const { t } = useTranslation(['creator', 'common']);
  const navigate = useNavigate();

  // ─────────────────────────────────────────
  // Audio state — multi-file + multi-range
  // ─────────────────────────────────────────
  const [audioFiles, setAudioFiles] = useState<File[]>([]);
  const [mergedAudioUrl, setMergedAudioUrl] = useState<string | null>(null);
  const [mergedAudioDuration, setMergedAudioDuration] = useState<number>(0);
  const [audioRanges, setAudioRanges] = useState<AudioRange[]>([]);

  // Phase tracking: 'select' = picking files, 'range' = ranges on merged audio
  // If draft already has uploaded_audio_url, we resume in 'range' phase.
  const [audioPhase, setAudioPhase] = useState<'select' | 'range'>('select');

  // Upload/processing state
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [processingAudio, setProcessingAudio] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState<string>('');
  const [submitting, setSubmitting] = useState(false); // For AI generation path

  // Guard to prevent double-click issues
  const isProcessingRef = useRef(false);

  // Refs to always get latest callback functions (to avoid stale closures)
  const onAdvanceStageRef = useRef(onAdvanceStage);
  const onUpdateDraftRef = useRef(onUpdateDraft);
  useEffect(() => {
    onAdvanceStageRef.current = onAdvanceStage;
    onUpdateDraftRef.current = onUpdateDraft;
  }, [onAdvanceStage, onUpdateDraft]);

  // Text content state
  const [englishText, setEnglishText] = useState('');
  const [chineseText, setChineseText] = useState('');

  // Detected material source
  const [detectedPath, setDetectedPath] = useState<MaterialSourceType>('ai_generated');

  // ─────────────────────────────────────────
  // Hydrate from draft on mount / draft change
  // ─────────────────────────────────────────
  useEffect(() => {
    if (!draft) return;
    setEnglishText(draft.uploaded_text_en || '');
    setChineseText(draft.uploaded_translation || '');

    if (draft.uploaded_audio_url) {
      setMergedAudioUrl(resolveMediaUrl(draft.uploaded_audio_url));
      setAudioPhase('range');
    } else {
      setMergedAudioUrl(null);
      setAudioPhase('select');
    }
    // We watch specific draft fields rather than the whole draft object —
    // some fields like `last_saved_at` change every poll and would re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draft?.uploaded_audio_url, draft?.uploaded_text_en, draft?.uploaded_translation]);

  // ─────────────────────────────────────────
  // Detect material source based on what's provided
  // ─────────────────────────────────────────
  useEffect(() => {
    const hasAudio = audioFiles.length > 0 || !!mergedAudioUrl || !!draft?.uploaded_audio_url;
    const hasEnglish = englishText.trim().length > 0;
    const hasChinese = chineseText.trim().length > 0;

    let newPath: MaterialSourceType = 'ai_generated';
    if (hasAudio && hasEnglish && hasChinese) newPath = 'audio_en_translation';
    else if (hasAudio && hasEnglish) newPath = 'audio_en';
    else if (hasAudio && hasChinese) newPath = 'audio_translation';
    else if (hasAudio) newPath = 'audio_only';
    else if (hasEnglish) newPath = 'text_en';
    else if (hasChinese) newPath = 'text_translation';

    setDetectedPath(newPath);
  }, [audioFiles, mergedAudioUrl, englishText, chineseText, draft?.uploaded_audio_url]);

  // ─────────────────────────────────────────
  // Audio Phase 1: merge files server-side
  // ─────────────────────────────────────────
  const handleMergeAudioFiles = async () => {
    if (audioFiles.length === 0) return;
    setMerging(true);
    setMergeError(null);

    try {
      // Ensure draft exists
      let currentDraft = draft;
      if (!currentDraft) {
        currentDraft = await onCreateDraft({
          cefr_level: 'B1',
          content_type: 'dialogue',
          title: '',
          description: '',
          tags: [],
        });
        if (!currentDraft) throw new Error('Failed to create draft');
      }

      const result = await creatorWorkflowService.uploadAudioFiles(currentDraft.id, audioFiles);
      setMergedAudioUrl(resolveMediaUrl(result.mergedAudioUrl));
      setMergedAudioDuration(result.durationSeconds);
      setAudioRanges([]);
      setAudioPhase('range');

      // Refresh draft so uploaded_audio_url shows in subsequent renders
      if (onRefreshDraft) await onRefreshDraft();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to merge audio';
      setMergeError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setMerging(false);
    }
  };

  // ─────────────────────────────────────────
  // Re-upload — reset to 'select' phase
  // ─────────────────────────────────────────
  const handleReupload = () => {
    setAudioFiles([]);
    setMergedAudioUrl(null);
    setMergedAudioDuration(0);
    setAudioRanges([]);
    setAudioPhase('select');
    setMergeError(null);
  };

  // ─────────────────────────────────────────
  // Range callbacks
  // ─────────────────────────────────────────
  const handleRangesChange = useCallback((ranges: AudioRange[]) => {
    setAudioRanges(ranges);
  }, []);

  const handleDurationLoaded = useCallback((duration: number) => {
    setMergedAudioDuration(duration);
  }, []);

  // ─────────────────────────────────────────
  // Proceed to next stage
  // ─────────────────────────────────────────
  const handleProceed = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const hasMergedAudio = !!mergedAudioUrl || !!draft?.uploaded_audio_url;
    const hasEnglish = englishText.trim().length > 0;
    const hasChinese = chineseText.trim().length > 0;

    let currentPath: MaterialSourceType = 'ai_generated';
    if (hasMergedAudio && hasEnglish && hasChinese) currentPath = 'audio_en_translation';
    else if (hasMergedAudio && hasEnglish) currentPath = 'audio_en';
    else if (hasMergedAudio && hasChinese) currentPath = 'audio_translation';
    else if (hasMergedAudio) currentPath = 'audio_only';
    else if (hasEnglish) currentPath = 'text_en';
    else if (hasChinese) currentPath = 'text_translation';

    // Ensure draft exists
    let currentDraft = draft;
    if (!currentDraft) {
      try {
        const newDraft = await onCreateDraft({
          cefr_level: 'B1',
          content_type: 'dialogue',
          title: '',
          description: '',
          tags: [],
        });
        if (!newDraft) {
          alert('Failed to create draft. Please try again.');
          isProcessingRef.current = false;
          return;
        }
        currentDraft = newDraft;
      } catch (error: any) {
        console.error('Failed to create draft:', error);
        alert(`Failed to create draft: ${error.message}`);
        isProcessingRef.current = false;
        return;
      }
    }

    try {
      const isAudioPath = ['audio_only', 'audio_en', 'audio_translation', 'audio_en_translation'].includes(currentPath);

      if (isAudioPath) {
        if (!hasMergedAudio) {
          alert(t('creator:workflow.audioUpload.mergeFirst'));
          isProcessingRef.current = false;
          return;
        }

        setUploadingAudio(true);
        setAudioUploadProgress(t('creator:workflow.stage0.uploading'));
        onSetGlobalProcessing?.(true, t('creator:workflow.stage0.loadingOverlay'));

        try {
          // Persist text materials (will be referenced during subtitle editing)
          if (hasEnglish || hasChinese) {
            await onUpdateDraftRef.current({
              material_source: currentPath,
              uploaded_text_en: englishText.trim() || null,
              uploaded_translation: chineseText.trim() || null,
            });
          }

          const result = await creatorWorkflowService.transcribeAudio(
            currentDraft.id,
            audioRanges.length > 0 ? audioRanges : undefined,
          );

          setAudioUploadProgress(`Transcription complete! ${result.subtitles.length} subtitles generated.`);
          onSetGlobalProcessing?.(false);

          if (currentDraft.conversational_mode !== false) {
            // Conversational mode (default): backend already advanced the stage —
            // refresh draft so StageContent renders the new stage.
            setTimeout(async () => {
              if (onRefreshDraft) {
                await onRefreshDraft();
              } else {
                window.location.reload();
              }
            }, 300);
          } else {
            // Classic mode: navigate to subtitle editor page
            const draftIdForNav = currentDraft.id;
            setTimeout(() => {
              navigate(`/creator/wizard/${draftIdForNav}/subtitles`);
            }, 500);
          }

          isProcessingRef.current = false;
          return;
        } catch (error: any) {
          console.error('Transcribe failed:', error);
          setUploadingAudio(false);
          onSetGlobalProcessing?.(false);
          alert(`Transcribe failed: ${error.response?.data?.message || error.message}`);
          isProcessingRef.current = false;
          return;
        }
      }

      // Text-only / AI-generated paths
      setSubmitting(true);
      await onUpdateDraftRef.current({
        material_source: currentPath,
        uploaded_text_en: englishText.trim() || null,
        uploaded_translation: chineseText.trim() || null,
      });
      await onAdvanceStageRef.current();
      isProcessingRef.current = false;
    } catch (error: any) {
      console.error('Proceed failed:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
      setUploadingAudio(false);
      setProcessingAudio(false);
      setSubmitting(false);
      isProcessingRef.current = false;
    }
  };

  // ─────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────
  return (
    <div className="stage-0-material-input">
      <header className="stage-header">
        <h2>{t('creator:workflow.stage0.title')}</h2>
        <p>{t('creator:workflow.stage0.description')}</p>
      </header>

      <div className="form-section materials-section">
        <h3>{t('creator:workflow.stage0.uploadExisting')}</h3>
        <p className="section-description">
          {t('creator:workflow.stage0.description')}
        </p>

        {/* Audio section */}
        <div className="material-upload-group">
          <label>
            <strong>{t('creator:workflow.stage0.audioUpload')}</strong>
            {' '}{t('creator:workflow.stage0.audioHint')}
          </label>

          {audioPhase === 'select' && (
            <div className="upload-area">
              <MultiAudioFileList
                files={audioFiles}
                onChange={setAudioFiles}
                disabled={merging}
              />
              {mergeError && (
                <div className="multi-audio-error" role="alert" style={{ marginTop: '0.75rem' }}>
                  {mergeError}
                </div>
              )}
              {audioFiles.length > 0 && (
                <div className="audio-merge-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleMergeAudioFiles}
                    disabled={merging}
                  >
                    {merging
                      ? t('creator:workflow.audioUpload.merging')
                      : t('creator:workflow.audioUpload.continueToRange')}
                  </button>
                </div>
              )}
            </div>
          )}

          {audioPhase === 'range' && mergedAudioUrl && (
            <div className="upload-area">
              <div className="audio-preview-header">
                <button
                  type="button"
                  className="btn-remove"
                  onClick={handleReupload}
                >
                  {t('creator:workflow.audioUpload.reupload')}
                </button>
              </div>
              <AudioRangeSelector
                audioUrl={mergedAudioUrl}
                duration={mergedAudioDuration}
                onRangesChange={handleRangesChange}
                onDurationLoaded={handleDurationLoaded}
                initialRanges={audioRanges.length > 0 ? audioRanges : undefined}
              />
            </div>
          )}
        </div>

        {/* English Transcript */}
        <div className="material-upload-group">
          <label htmlFor="english-text">
            <strong>{t('creator:workflow.stage0.englishTranscript', 'English Transcript')}</strong>
          </label>
          <textarea
            id="english-text"
            value={englishText}
            onChange={(e) => setEnglishText(e.target.value)}
            placeholder={t('creator:workflow.stage0.textPlaceholder')}
            rows={6}
          />
          {englishText && (
            <div className="text-stats">
              {t('creator:workflow.stage0.wordsCount')} {englishText.split(/\s+/).filter(w => w).length} |
              {t('creator:workflow.stage0.charsCount')} {englishText.length}
            </div>
          )}
        </div>

        {/* Chinese Translation */}
        <div className="material-upload-group">
          <label htmlFor="chinese-text">
            <strong>{t('creator:workflow.stage0.chineseTranslation', 'Chinese Translation')}</strong>
          </label>
          <textarea
            id="chinese-text"
            value={chineseText}
            onChange={(e) => setChineseText(e.target.value)}
            placeholder={t('creator:workflow.stage0.translationPlaceholder')}
            rows={6}
          />
          {chineseText && (
            <div className="text-stats">
              {t('creator:workflow.stage0.charsCount')} {chineseText.length}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="form-actions">
        <button
          type="button"
          className="btn-primary btn-large"
          onClick={handleProceed}
          disabled={loading || uploadingAudio || processingAudio || submitting || merging}
        >
          {uploadingAudio
            ? t('creator:workflow.stage0.uploading')
            : processingAudio
              ? t('common:processing')
              : (loading || submitting)
                ? t('common:processing')
                : detectedPath === 'ai_generated'
                  ? t('common:next')
                  : detectedPath === 'audio_only'
                    ? t('common:upload')
                    : t('common:next')}
        </button>

        {audioUploadProgress && (
          <p className="upload-progress-message">{audioUploadProgress}</p>
        )}
      </div>

      {(loading || uploadingAudio || processingAudio || submitting) && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>
            {uploadingAudio && !processingAudio
              ? t('creator:workflow.stage0.uploading')
              : processingAudio
                ? audioUploadProgress || t('creator:workflow.stage0.loadingOverlay')
                : submitting
                  ? t('common:processing')
                  : t('creator:workflow.stage0.loadingOverlay')}
          </p>
          {processingAudio && (
            <p className="loading-subtitle">
              {t('creator:workflow.stage0.loadingOverlay')}
            </p>
          )}
          {submitting && (
            <p className="loading-subtitle">
              {t('common:processing')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Stage0MaterialInput;
